import { EditIcon, FactoryIcon } from "lucide-react";

import { CompanyProfileSectionTitle } from "./CompanyProfileSectionTitle";
import { Column, EmptyState, RichTextDisplay, Row, Text } from "@/components";
import { CompanyLogosSection } from "@/components/companies/profile";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { paymentTermsToLabel } from "@/lib/constants";
import { cn } from "@/lib/utils";

import { CompanyInformation, CompanySummary } from "@/types";

const CompanyDetail = ({ title, value }: { title: string; value: string }) => {
  return (
    <Column className="gap-2">
      <Text>{title}</Text>
      <p className="text-sm text-slate-700">{value}</p>
    </Column>
  );
};

const BadgeListSection = ({
  title,
  items,
}: {
  title: string;
  items: string[];
}) => {
  // This is intentional because we want to hide the specific badge list section if there are no items.
  if (items.length === 0) return null;

  return (
    <Column className="gap-2">
      <CompanyProfileSectionTitle title={title} />
      <Row className="flex-wrap gap-1">
        {items.map((item) => (
          <Badge className="bg-slate-50" key={item} variant="outline">
            {item}
          </Badge>
        ))}
      </Row>
    </Column>
  );
};

const isCompanyInformationEmpty = (
  companyInformation: CompanyInformation | null,
): boolean => {
  if (!companyInformation?.companyInformation) return true;

  const info = companyInformation.companyInformation;
  return (
    !info.logoCompanyFileId &&
    !info.smallLogoCompanyFileId &&
    !info.address &&
    !info.description &&
    info.naicsCodes.length === 0 &&
    info.customerIndustries.length === 0 &&
    info.manufacturingCapabilities.length === 0 &&
    info.equipment.length === 0 &&
    info.materials.length === 0 &&
    !info.headcount &&
    !info.foundedYear &&
    !info.location &&
    !info.dunsNumber &&
    !info.cageCode &&
    !info.websiteUrl &&
    !info.companyAbbreviation
  );
};

export const CompanyProfileInformation = ({
  className,
  company,
  companyInformation,
  logoSignedUrl,
  setIsEditing,
}: {
  className?: string;
  company: CompanySummary;
  companyInformation: CompanyInformation | null;
  logoSignedUrl: string | null;
  setIsEditing: (isEditing: boolean) => void;
}) => {
  const render = () => {
    if (!companyInformation || isCompanyInformationEmpty(companyInformation)) {
      return (
        <EmptyState
          description="Details added to the company profile will appear here."
          handlePrimaryClick={() => {
            setIsEditing(true);
          }}
          Icon={FactoryIcon}
          primaryButtonName="Add Company Information"
          primaryButtonVariant="default"
          title={`Add a company profile for ${company.name}`}
        />
      );
    }

    // Destructure the company information to make it prettier
    const { companyInformation: companyInfo } = companyInformation;

    return (
      <Column className={cn("gap-8", className)}>
        <Row className="items-center justify-between">
          <Row className="items-center gap-4">
            {logoSignedUrl && (
              <img
                alt={`${company.name} logo`}
                className="h-20 w-20 object-contain"
                src={logoSignedUrl}
              />
            )}
            <Column>
              <p className="text-2xl font-semibold text-slate-700">
                {company.name} ({companyInfo.companyAbbreviation})
              </p>
              {companyInfo.websiteUrl && (
                <a
                  className="text-sm font-medium text-primary-700 hover:underline"
                  href={companyInfo.websiteUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {companyInfo.websiteUrl}
                </a>
              )}
              {companyInfo.address && (
                <Text variant="small">{companyInfo.address}</Text>
              )}
            </Column>
          </Row>
          <Button
            className="text-primary-900 hover:text-primary-900"
            onClick={() => {
              setIsEditing(true);
            }}
            variant="ghost"
          >
            <EditIcon className="h-4 w-4" /> Edit
          </Button>
        </Row>

        <CompanyLogosSection
            companyName={company.name}
            companyInformation={companyInformation}
            logoSignedUrl={logoSignedUrl}
          />

        {/* About Section */}
        {companyInfo.description && (
          <Column className="gap-4">
            <CompanyProfileSectionTitle title="About" />
            <RichTextDisplay
              className="font-medium text-slate-700"
              richText={companyInfo.description}
            />
          </Column>
        )}

        <BadgeListSection
          title="NAICS Code(s)"
          items={companyInfo.naicsCodes}
        />
        <BadgeListSection
          title="Industries of Our Customers"
          items={companyInfo.customerIndustries}
        />
        <BadgeListSection
          title="Manufacturing Capabilities / Products"
          items={companyInfo.manufacturingCapabilities}
        />
        <BadgeListSection title="Equipment" items={companyInfo.equipment} />
        <BadgeListSection title="Materials" items={companyInfo.materials} />

        {/* Company Details Section */}
        {(companyInfo.headcount ??
          companyInfo.foundedYear ??
          companyInfo.location ??
          companyInfo.dunsNumber ??
          companyInfo.cageCode) && (
          <Column className="gap-2">
            <CompanyProfileSectionTitle title="Company Details" />
            <div className="grid w-[83%] grid-cols-3 gap-4">
              {companyInfo.headcount && (
                <CompanyDetail
                  title="Headcount"
                  value={companyInfo.headcount.toLocaleString()}
                />
              )}
              {companyInfo.foundedYear && (
                <CompanyDetail
                  title="Founded In"
                  value={companyInfo.foundedYear}
                />
              )}
              {companyInfo.location && (
                <CompanyDetail title="Location" value={companyInfo.location} />
              )}
              {companyInfo.dunsNumber && (
                <CompanyDetail
                  title="D-U-N-S Number"
                  value={companyInfo.dunsNumber}
                />
              )}
              {companyInfo.cageCode && (
                <CompanyDetail title="Cage Code" value={companyInfo.cageCode} />
              )}
              <CompanyDetail
                title="Payment Terms"
                value={paymentTermsToLabel[companyInfo.paymentTerms]}
              />
            </div>
          </Column>
        )}
      </Column>
    );
  };

  return render();
};
